/**
 * Presentation contract for one or more trackers (ENG-2812 / ENG-2814 / ENG-2815).
 * Collapse is count-first — never `{name} +N` (names often end in digits).
 */

export type NamedTracker = {
  id: number;
  name?: string | null;
  serial?: string | null;
  imei?: string | null;
};

export type TrackerSetView =
  | { kind: 'empty' }
  | { kind: 'single'; tracker: NamedTracker; label: string }
  | {
      kind: 'many';
      count: number;
      trackers: NamedTracker[];
      labels: string[];
      collapsedLabel: string;
    };

/** Customer-facing label: name → serial → `#id`. */
export function namedTrackerLabel(tracker: NamedTracker): string {
  const name = tracker.name?.trim();
  if (name) return name;
  const serial = tracker.serial?.trim();
  if (serial) return serial;
  return `#${tracker.id}`;
}

export function collapsedTrackerSetLabel(count: number): string {
  if (count <= 0) return '—';
  if (count === 1) return '1 tracker';
  return `${count} trackers`;
}

export function buildTrackerSetView(
  trackers: readonly NamedTracker[],
): TrackerSetView {
  if (trackers.length === 0) {
    return { kind: 'empty' };
  }
  if (trackers.length === 1) {
    const tracker = trackers[0]!;
    return {
      kind: 'single',
      tracker,
      label: namedTrackerLabel(tracker),
    };
  }
  const labels = trackers.map(namedTrackerLabel);
  return {
    kind: 'many',
    count: trackers.length,
    trackers: [...trackers],
    labels,
    collapsedLabel: collapsedTrackerSetLabel(trackers.length),
  };
}

function firstNonEmpty(
  ...values: Array<string | null | undefined>
): string | null {
  for (const value of values) {
    if (value != null && String(value).trim() !== '') {
      return value;
    }
  }
  return null;
}

function mergeNamedTracker(
  current: NamedTracker,
  incoming: NamedTracker,
): NamedTracker {
  return {
    id: current.id,
    name: firstNonEmpty(current.name, incoming.name),
    serial: firstNonEmpty(current.serial, incoming.serial),
    imei: firstNonEmpty(current.imei, incoming.imei),
  };
}

export function namedTrackersFromIds(
  ids: readonly number[],
  sources: readonly NamedTracker[],
): NamedTracker[] {
  const byId = new Map<number, NamedTracker>();
  for (const source of sources) {
    if (source.id <= 0) continue;
    const existing = byId.get(source.id);
    byId.set(
      source.id,
      existing ? mergeNamedTracker(existing, source) : source,
    );
  }
  const seen = new Set<number>();
  const out: NamedTracker[] = [];
  for (const id of ids) {
    if (typeof id !== 'number' || id <= 0 || seen.has(id)) continue;
    seen.add(id);
    const source = byId.get(id);
    out.push(
      source ?? {
        id,
      },
    );
  }
  return out;
}

export function namedTrackersFromShipment(shipment: {
  devices?: readonly NamedTracker[] | null;
  device_ids?: readonly number[] | null;
}): NamedTracker[] {
  const embedded = shipment.devices ?? [];
  const ids =
    shipment.device_ids ??
    embedded.map((d) => d.id).filter((id) => typeof id === 'number' && id > 0);
  return namedTrackersFromIds(ids, embedded);
}

/** Map device-list rows (`serial` or legacy `serial_number`) into NamedTracker. */
export function namedTrackerFromDeviceListRow(row: {
  id: number;
  name?: string | null;
  serial?: string | null;
  serial_number?: string | null;
  imei?: string | null;
}): NamedTracker {
  return {
    id: row.id,
    name: row.name ?? null,
    serial: firstNonEmpty(row.serial, row.serial_number),
    imei: row.imei ?? null,
  };
}

/**
 * Label lookup with the same merge rules as {@link namedTrackersFromIds}
 * (later sources fill missing name/serial/imei).
 */
export function trackerLabelResolverFromSources(
  sources: readonly NamedTracker[],
): (deviceId: number) => string {
  const ids = [
    ...new Set(
      sources
        .map((source) => source.id)
        .filter((id) => typeof id === 'number' && id > 0),
    ),
  ];
  const byId = new Map(
    namedTrackersFromIds(ids, sources).map((tracker) => [tracker.id, tracker]),
  );
  return (deviceId: number) =>
    namedTrackerLabel(byId.get(deviceId) ?? { id: deviceId });
}
